import { CompanyType, permissions, WithPermission, WithRestriction } from '@utilities-one/api-sdk';

import { CHANGE_ORDERS_VIEW_PERMISSIONS } from '@/entities/projectChangeOrder/utils/permissions';
import { hasPermission } from '@/entities/user/utils/access';

export const routeAccess: WithRestriction<WithPermission<{ pathname: string; exact?: boolean }>>[] = [
  {
    pathname: '/dashboard',
    exact: true,
    permission: permissions.analytics.dashboards.mainDashboard.view,
  },
  {
    pathname: '/dashboard/osp',
    permission: permissions.analytics.dashboards.ospDashboard.view,
  },
  {
    pathname: '/dashboard/client',
    permission: permissions.analytics.dashboards.clientDashboard.view,
  },
  {
    pathname: '/dashboard/regions-structure',
    permission: permissions.analytics.dashboards.regionsStructure.view,
  },
  {
    pathname: '/clients',
    permission: 'clients',
  },
  {
    pathname: '/clients/[id]/update',
    permission: permissions.clients.update,
  },
  {
    // Rules are prefix rules unless `exact` is set, so this one already guards
    // every nested contract route (/view, /history, /taskManagement, ...) with
    // clients.contracts.view. The entries below only ADD the permission their
    // own sub-page needs on top of it.
    pathname: '/clients/[id]/contracts',
    permission: permissions.clients.contracts.view,
  },
  {
    pathname: '/clients/[id]/contracts/create',
    permission: permissions.clients.contracts.update,
  },
  {
    pathname: '/clients/[id]/contracts/[id]/update',
    permission: permissions.clients.contracts.update,
  },
  {
    pathname: '/clients/[id]/contracts/[id]/taskManagement',
    permission: permissions.clients.taskManagement.view,
  },
  {
    pathname: '/clients/[id]/contracts/[id]/jobSpecifications',
    permission: permissions.clients.jobSpecifications.view,
  },
  {
    pathname: '/clients/[id]/contracts/[id]/jobSpecifications/create',
    permission: permissions.clients.jobSpecifications.create,
  },
  {
    pathname: '/clients/[id]/contracts/[id]/jobSpecifications/[id]/update',
    permission: permissions.clients.jobSpecifications.update,
  },
  {
    pathname: '/clients/[id]/jobSpecifications',
    permission: permissions.clients.jobSpecifications.view,
  },
  {
    pathname: '/clients/[id]/jobSpecifications/create',
    permission: permissions.clients.jobSpecifications.create,
  },
  {
    pathname: '/clients/[id]/jobSpecifications/[id]/update',
    permission: permissions.clients.jobSpecifications.update,
  },
  {
    pathname: '/projects',
    permission: 'projects',
  },
  {
    pathname: '/projects/[id]/map',
    permission: 'projects.view',
  },
  {
    pathname: '/projectManagement/create',
    permission: permissions.projectManagement.create,
  },
  {
    pathname: '/projectManagement/[id]/update/[stage]',
    permission: permissions.projectManagement.update,
  },
  {
    pathname: '/projectManagement/[id]/update/revenueForecasts',
    permission: permissions.projectManagement.financialForecast.viewRevenue,
  },
  {
    pathname: '/projectManagement/[id]/update/laborForecasts',
    permission: permissions.projectManagement.financialForecast.viewLabor,
  },
  {
    pathname: '/projectManagement/[id]/update/managementLabor',
    permission: permissions.projectManagement.financialForecast.viewManagementLabor,
  },
  {
    pathname: '/projectManagement/[id]/update/overallCosts',
    permission: permissions.projectManagement.financialForecast.viewOverallCosts,
  },
  {
    pathname: '/projectManagement/[id]/update/scheduleExecution',
    permission: permissions.projectManagement.financialForecast.viewScheduleExecution,
  },
  {
    pathname: '/projectManagement/[id]/update/monthlyPL',
    permission: permissions.projectManagement.financialForecast.viewMonthlyPL,
  },
  {
    pathname: '/projectManagement/[id]/update/summary',
    permission: permissions.projectManagement.financialForecast.viewSummary,
  },
  {
    // Any-of: manage implies view (UG-5190).
    pathname: '/projectManagement/[id]/update/changeOrders',
    permission: CHANGE_ORDERS_VIEW_PERMISSIONS,
  },
  {
    pathname: '/projects/[id]/reels',
    permission: permissions.projects.reels.view,
  },
  {
    pathname: '/reports/jobs',
    permission: 'jobs.view',
  },
  {
    pathname: '/jobSpecForecast',
    permission: permissions.forecasts.jobSpecificationsForecast.view,
  },
  {
    pathname: '/reports/clients-vendor-rate-cards',
    permission: 'analytics.reports.clientVendorRateCards',
  },
  {
    pathname: '/notifications',
    permission: 'notifications',
  },
  {
    pathname: '/users',
    permission: 'settings.users',
  },
  {
    pathname: '/users/create',
    permission: permissions.settings.users.create,
  },
  {
    pathname: '/users/history',
    permission: permissions.settings.users.view,
  },
  {
    pathname: '/users/[id]/details',
    permission: permissions.settings.users.view,
  },
  {
    pathname: '/users/[id]/update',
    permission: permissions.settings.users.update,
  },
  {
    pathname: '/users/[id]/permissions',
    permission: permissions.settings.users.update,
  },
  {
    pathname: '/users/[id]/history',
    permission: permissions.settings.users.view,
  },
  {
    pathname: '/roles',
    permission: 'settings.roles',
  },
  {
    pathname: '/roles/create',
    permission: permissions.settings.roles.create,
  },
  {
    pathname: '/roles/[id]/update',
    permission: permissions.settings.roles.update,
  },
  {
    pathname: '/sessions',
    permission: 'settings.sessions',
  },
  {
    pathname: '/system/env',
    permission: permissions.settings.system.env,
  },
  {
    pathname: '/companies',
    permission: 'companies',
  },
  {
    pathname: '/companies/create',
    permission: permissions.companies.create,
  },
  {
    pathname: '/companies/[id]/update',
    permission: permissions.companies.update,
  },
  {
    pathname: '/companies/[id]/clients',
    permission: permissions.companies.clients.view,
  },
  {
    // The company-side Contracts tab renders the CLIENT contract grid.
    pathname: '/companies/[id]/clients/[id]/contracts',
    permission: permissions.clients.contracts.view,
  },
  {
    // ...but its rows link through to that contract's COMPANY job specifications.
    pathname: '/companies/[id]/clients/[id]/contracts/[id]/jobSpecifications',
    permission: permissions.companies.jobSpecifications.view,
  },
  {
    pathname: '/companies/[id]/clients/[id]/contracts/[id]/jobSpecifications/create',
    permission: permissions.companies.jobSpecifications.create,
  },
  {
    pathname: '/companies/[id]/clients/[id]/companyContracts',
    permission: permissions.companies.contracts.view,
  },
  {
    pathname: '/companies/[id]/clients/[id]/companyContracts/create',
    permission: permissions.companies.contracts.update,
  },
  {
    pathname: '/companies/[id]/clients/[id]/companyContracts/[id]/update',
    permission: permissions.companies.contracts.update,
  },
  {
    pathname: '/companies/[id]/clients/[id]/companyContracts/[id]/jobSpecifications',
    permission: permissions.companies.jobSpecifications.view,
  },
  {
    pathname: '/companies/[id]/clients/[id]/companyContracts/[id]/jobSpecifications/create',
    permission: permissions.companies.jobSpecifications.create,
  },
  {
    pathname: '/companies/[id]/clients/[id]/companyContracts/[id]/jobSpecifications/managePrices',
    permission: permissions.companies.jobSpecifications.update,
  },
  {
    pathname: '/companies/[id]/clients/[id]/jobSpecifications',
    permission: permissions.companies.jobSpecifications.view,
  },
  {
    pathname: '/companies/[id]/clients/[id]/jobSpecifications/[id]/update',
    permission: permissions.companies.jobSpecifications.update,
  },
  {
    pathname: '/companies/[id]/clients/[id]/jobSpecifications/managePrices',
    permission: permissions.companies.jobSpecifications.update,
  },
  {
    pathname: '/damages',
    permission: permissions.damageDrafts.view,
  },
  {
    pathname: '/damages/create',
    permission: permissions.damageDrafts.create,
  },
  {
    pathname: '/damages/[damageId]/update',
    permission: permissions.damageDrafts.update,
  },
  {
    pathname: '/gigs',
    permission: 'gigs',
  },
  {
    pathname: '/restorations',
    permission: 'restorations',
  },
  {
    pathname: '/per-diem',
    permission: 'perDiem.view',
  },
  {
    pathname: '/per-diem',
    permission: 'perDiem.view',
  },
  {
    pathname: '/per-diem/create',
    permission: permissions.perDiem.create,
    restriction: (user) => user.company.type === CompanyType.inhouse,
  },
  {
    pathname: '/invoices/create',
    restriction: (user) =>
      hasPermission(user, permissions.userProfile.companyScoped) && user.company.type === CompanyType.externalVendor,
    permission: permissions.companyInvoices.create,
  },
  {
    pathname: '/invoices',
    permission: permissions.companyInvoices.view,
  },
  {
    pathname: '/invoices/[id]/details/invoice',
    permission: permissions.companyInvoices.view,
  },
  {
    pathname: '/invoices/[id]/details/creditMemo',
    permission: permissions.companyInvoices.view,
  },

  {
    pathname: '/check-in',
    permission: [permissions.dailyCheckIn.checkIn.viewAll, permissions.dailyCheckIn.checkIn.viewAsForeman],
  },
  {
    pathname: '/activities',
    permission: [permissions.dailyCheckIn.activities.viewAll, permissions.dailyCheckIn.activities.viewAsForeman],
  },
  {
    pathname: '/remittance',
    permission: [permissions.remittance.view],
  },
  {
    pathname: '/remittance/create',
    permission: permissions.remittance.create,
  },
  {
    pathname: '/profile/clients',
    permission: [permissions.clients.view, permissions.userProfile.clients.view],
  },
  {
    pathname: '/profile/clients/[clientId]/companyContracts',
    // Must mirror the companyContracts/getAll guard, or the page loads and every request 403s.
    permission: [permissions.companies.contracts.view, permissions.userProfile.clients.view],
  },
  {
    pathname: '/velocity-forecast',
    permission: permissions.forecasts.velocityForecast.view,
  },
  {
    pathname: '/velocity-forecast/create',
    permission: permissions.forecasts.velocityForecast.manage,
  },
  {
    pathname: '/readinessForecast',
    permission: permissions.forecasts.readinessForecast.view,
  },
  {
    pathname: '/revenue-forecast',
    permission: 'analytics.reports.revenueForecast',
  },
  {
    pathname: '/reports/workable-forecast-report',
    permission: 'analytics.reports.workableForecastReport',
  },
  {
    pathname: '/reports/workforce-forecast',
    permission: 'analytics.reports.workforceForecast',
  },
  {
    pathname: '/revenue-report',
    permission: 'analytics.reports.revenueReport',
  },
  {
    pathname: '/reports/tti-report',
    permission: 'analytics.reports.timeToInvoice',
  },
  {
    pathname: '/cop',
    permission: permissions.cop.view,
  },
  {
    pathname: '/reports/tti-report',
    permission: permissions.analytics.reports.timeToInvoice,
  },
  {
    pathname: '/reports/vendor-report',
    permission: permissions.analytics.reports.vendorReport,
  },
  {
    pathname: '/reports/projects-execution-forecast',
    permission: permissions.analytics.reports.projectsExecutionForecast,
  },
  {
    pathname: '/reports/projects-revenue-forecast',
    permission: permissions.analytics.reports.projectsRevenueForecast,
  },
  {
    pathname: '/cop',
    permission: permissions.cop.view,
  },
  {
    pathname: '/drive',
    permission: permissions.drive.view,
  },
  {
    pathname: '/gpm-drive/all-media',
    permission: permissions.media.view,
  },
  {
    pathname: '/gpm-drive/drive',
    permission: permissions.drive.view,
  },
  {
    pathname: '/media',
    permission: permissions.media.view,
  },
  {
    pathname: '/licenses',
    permission: permissions.licenses.view,
  },
  {
    pathname: '/timesheets',
    permission: [
      permissions.timeSheets.view.own,
      permissions.timeSheets.view.hierarchy,
      permissions.timeSheets.view.all,
    ],
  },
  {
    pathname: '/timesheets/manage',
    permission: [permissions.timeSheets.view.all, permissions.timeSheets.view.hierarchy],
  },
  {
    pathname: '/timesheets/[userId]/view',
    permission: [
      permissions.timeSheets.view.own,
      permissions.timeSheets.view.hierarchy,
      permissions.timeSheets.view.all,
    ],
  },
  {
    pathname: '/timesheets/[userId]/view/history',
    permission: [
      permissions.timeSheets.view.own,
      permissions.timeSheets.view.hierarchy,
      permissions.timeSheets.view.all,
    ],
  },
];
